import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';
import EntityDetailDrawer from '../EntityDetailDrawer';
import { PanelError } from '../Benchmark/BenchmarkPrimitives';
import { InsightMeta, Item, campaignInsightsService } from '../../services/campaignInsightsService';
import { InsightGroupItems } from './InsightItems';
import GroupReasoningSummary from '../Explainability/GroupReasoningSummary';
import { dsVar } from '../../theme/tokens';

/** The group a summary row opened. */
export interface OpenInsightGroup {
    kind: 'group';
    capsuleId: string;
    groupIdentifier: string;
    groupName: string;
    /** The capsule's activity, which narrows the group's reasoning to the step behind this row. */
    activityTemplateCode?: string;
}

/** The insight an agent reference opened; its campaign and capsule are known once it loads. */
export interface OpenAgentInsight {
    kind: 'insight';
    insightId: string;
    title: string;
    informationCategory?: string;
}

export type OpenInsight = OpenInsightGroup | OpenAgentInsight;

/** The group whose reasoning the drawer can show or hand over to the Explainability tab. */
export interface ReasoningTarget {
    campaignId: string;
    groupIdentifier: string;
    groupName: string;
    activityTemplateCode?: string;
}

/** Where an opened group or insight was produced; either may be unknown for an insight. */
export interface InsightSource {
    campaignId?: string;
    capsuleId?: string;
}

/** Scopes everything the drawer loads and mounts to one opening. */
export const openInsightKey = (opened: OpenInsight): string =>
    opened.kind === 'group' ? `group:${opened.capsuleId}:${opened.groupIdentifier}` : `insight:${opened.insightId}`;

interface InsightGroupDrawerProps {
    /**
     * The campaign the page shows. Fetching a group needs it. An insight names
     * its own, and falls back to this one when it does not.
     */
    campaignId: string | undefined;
    group: OpenInsight | null;
    onClose: () => void;
    /** A review decision was recorded for the group or insight produced here. */
    onReviewSubmitted: (source: InsightSource) => void;
    /**
     * The action that opens this group's full reasoning on the Explainability
     * tab, or undefined where the page cannot reach it.
     */
    showReasoningAction?: (target: ReasoningTarget) => (() => void) | undefined;
    /** Whether the drawer is at its wide width; the page moves the chat assistant aside for it. */
    expanded?: boolean;
    onToggleExpand?: () => void;
}

interface Loaded {
    key: string;
    items: Item[];
    meta?: InsightMeta;
}

/**
 * One group's full output, fetched when its summary row (or an assistant
 * reference to it) is opened, followed by why the agents reached it.
 *
 * Docked only on wide screens: the Insights tab already gives a column to the
 * chat assistant, so below 1440px the drawer overlays rather than squeezing the
 * summary table to nothing.
 */
const InsightGroupDrawer: React.FC<InsightGroupDrawerProps> = ({
    campaignId, group, onClose, onReviewSubmitted, showReasoningAction, expanded, onToggleExpand,
}) => {
    const [loaded, setLoaded] = useState<Loaded | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    const key = group ? openInsightKey(group) : null;

    useEffect(() => {
        if (!group || !key) return;
        if (group.kind === 'group' && !campaignId) return;
        let cancelled = false;
        setLoaded(null);
        setError(null);
        const request: Promise<Omit<Loaded, 'key'>> = group.kind === 'group'
            ? campaignInsightsService
                .getOutputGroup({ campaignId: campaignId as string, capsuleId: group.capsuleId, groupIdentifier: group.groupIdentifier })
                .then(items => ({ items }))
            : campaignInsightsService.getInsight(group.insightId);
        request
            .then((res) => { if (!cancelled) setLoaded({ key, ...res }); })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || 'Could not load this group.');
            });
        return () => { cancelled = true; };
    }, [campaignId, group, key, attempt]);

    // Content still on hand from the previous opening is never shown under this one's title.
    const current = loaded && loaded.key === key ? loaded : null;
    const items = current?.items ?? null;

    // A reference names its group and where it was produced only once the insight has loaded.
    const meta = current?.meta;
    const source: InsightSource = group?.kind === 'group'
        ? { campaignId, capsuleId: group.capsuleId }
        : { campaignId: meta?.campaignId ?? campaignId, capsuleId: meta?.capsuleId };
    let reasoning: ReasoningTarget | null = null;
    if (group?.kind === 'group' && campaignId) {
        reasoning = { campaignId, ...group };
    } else if (group?.kind === 'insight' && source.campaignId && meta?.groupIdentifier) {
        reasoning = {
            campaignId: source.campaignId,
            groupIdentifier: meta.groupIdentifier,
            groupName: meta.groupName || group.title,
            activityTemplateCode: meta.activityCode,
        };
    }
    const showFullReasoning = reasoning && showReasoningAction ? showReasoningAction(reasoning) : undefined;

    const title = group ? (group.kind === 'group' ? group.groupName : group.title) : '';
    const subtitle = group?.kind === 'insight'
        ? group.informationCategory || meta?.informationCategory
        : undefined;

    return (
        <EntityDetailDrawer
            open={Boolean(group)}
            onClose={onClose}
            docked
            dockedMinWidth={1440}
            width={560}
            expanded={expanded}
            onToggleExpand={onToggleExpand}
            icon={<DescriptionIcon />}
            title={title}
            subtitle={subtitle}
            description=""
        >
            {error ? (
                <PanelError message={error} onRetry={() => setAttempt(n => n + 1)} />
            ) : items === null ? (
                <Box className="flex items-center gap-3 py-10 justify-center">
                    <CircularProgress size={20} sx={{ color: dsVar('accent-fg') }} />
                    <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-2') }}>Loading details…</Typography>
                </Box>
            ) : items.length === 0 ? (
                <Typography sx={{ fontSize: '0.875rem', color: dsVar('fg-muted'), py: 4, textAlign: 'center' }}>
                    No details were recorded for this group.
                </Typography>
            ) : group && key && (
                <InsightGroupItems
                    key={`items-${key}`}
                    items={items}
                    groupKey={key}
                    onReviewSubmitted={() => onReviewSubmitted(source)}
                />
            )}
            {key && reasoning && (
                // Keyed apart from the items: siblings sharing a key leave the last group's content mounted on a switch.
                <GroupReasoningSummary
                    key={`reasoning-${key}`}
                    campaignId={reasoning.campaignId}
                    groupIdentifier={reasoning.groupIdentifier}
                    activityTemplateCode={reasoning.activityTemplateCode}
                    onShowFull={showFullReasoning}
                />
            )}
        </EntityDetailDrawer>
    );
};

export default InsightGroupDrawer;
